import React, { useEffect, useState } from "react";
import { ButtonGroup, Tab, Table, Tabs } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useAppApi } from "@/App";
import { useChoiceLabel } from "@/const";
import { useRequest } from "@components/AppHook";
import { DevForm } from "@components/Forms/Fields";
import { DefaultLayout } from "@components/Layouts";
import { ListingCard } from "@components/Pages";
import { Base64ToPDFBlob, url_reverse } from "@components/Utils";

export default function ProjectTransferModerationScreen() {
    const { t } = useTranslation();
    const params = useParams();
    const header = t("Модерация передачи проектов");
    return (
        <DefaultLayout>
            {["moder_fin", "moder_tech"].includes(params.smth) ? (
                <List header={header} kind={params.smth} />
            ) : (
                <Edit header={header} id={params.smth} />
            )}
        </DefaultLayout>
    );
}

function List({ header, kind }) {
    const { t } = useTranslation();
    const { apiData } = useAppApi(url_reverse("api:moderation:project_transfer:list", { kind }));
    const navigate = useNavigate();

    if (!apiData) return;

    const handleSelect = (k) => {
        navigate({
            pathname: url_reverse("admin:moderation:project_transfer:list", { kind: k }),
        });
    };

    const DevList = () => {
        return (
            <Table hover responsive className="dmg-table">
                <thead>
                    <tr>
                        <th>{t("Компания")}</th>
                        <th>{t("Дата переноса")}</th>
                    </tr>
                </thead>
                <tbody>
                    {apiData.object_list?.map((obj) => (
                        <tr key={obj.id}>
                            <td>
                                <Link
                                    to={url_reverse("admin:moderation:project_transfer:edit", { request_id: obj.id })}
                                >
                                    {obj.dev_transfer.name}
                                </Link>
                            </td>
                            <td>{obj.time_transfer}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        );
    };

    return (
        <ListingCard header={header}>
            <Tabs activeKey={kind} transition={false} className="react-bootstrap-tabs-fix" onSelect={handleSelect}>
                <Tab eventKey="moder_fin" title={t("Фин. модерация")}>
                    <DevList />
                </Tab>
                <Tab eventKey="moder_tech" title={t("Тех. модерация")}>
                    <DevList />
                </Tab>
            </Tabs>
        </ListingCard>
    );
}

function Edit({ id, header }) {
    const { t } = useTranslation();
    const { apiData, apiUrl } = useAppApi(url_reverse("api:moderation:project_transfer:moderate", { transfer_id: id }));
    const [moderationData, setModerationData] = useState(null);
    const DeveloperResidenceTerr = useChoiceLabel("DeveloperResidenceTerr");
    const GameTaxType = useChoiceLabel("GameTaxType");
    const { api_post } = useRequest();
    const navigate = useNavigate();

    useEffect(() => {
        if (!moderationData && apiData?.object_list) {
            const newGameData = { ...moderationData };
            newGameData.moder_comment = apiData.transfer.moder_comment;
            newGameData.game = {};
            apiData.object_list.forEach((obj) => {
                newGameData.game[obj.game_id] = {
                    merchant_set_id: obj.merchant_set_id || obj.game.merchant_set_id,
                    revenue_share: obj.revenue_share || obj.game.revenue_share,
                    tax_type: obj.tax_type || obj.game.tax_type,
                };
            });
            setModerationData(newGameData);
        }
    }, [apiData]);

    if (!apiData) return;
    if (!moderationData) return;

    const handleSubmit = (ev) => {
        const params = {
            id,
            state: ev.target.name,
            moder_comment: moderationData.moder_comment,
            game: Object.keys(moderationData.game).map(
                (gid) =>
                    `${gid};${moderationData.game[gid].merchant_set_id};${moderationData.game[gid].revenue_share};${
                        moderationData.game[gid].tax_type || ""
                    }`
            ),
        };
        api_post(ev, apiUrl, {
            params,
            then: (resp) => {
                if (resp.status === "ok") {
                    navigate(
                        url_reverse("admin:moderation:project_transfer:list", { kind: apiData.transfer.moder_status })
                    );
                }
            },
        });
    };

    return (
        <ListingCard header={header}>
            <Table hover responsive className="dmg-table">
                <thead>
                    <tr>
                        <th>{t("Игра")}</th>
                        <th>{t("Компания")}</th>
                        <th>{t("Территории резидентства")}</th>
                        <th>{t("Юридическое наименование")}</th>
                        <th>{t("Мерчант")}</th>
                        <th>{t("Доля с продаж")}</th>
                        <th>{t("Ставка НДС")}</th>
                    </tr>
                </thead>
                <tbody>
                    {apiData.object_list?.map((obj) => (
                        <tr key={obj.id}>
                            <td>
                                <Link to={url_reverse("games:admin", { game_id: obj.game_id })}>
                                    {obj.game.base_locale_object.name}
                                </Link>
                            </td>
                            <td>
                                <b>{t("было")}:</b>{" "}
                                <Link to={url_reverse("devs:edit", { dev_id: apiData.transfer.dev_transfer_id })}>
                                    {apiData.transfer.dev_transfer.name}
                                </Link>{" "}
                                <br />
                                <b>{t("стало")}:</b>{" "}
                                <Link to={url_reverse("devs:edit", { dev_id: apiData.transfer.dev_holder_id })}>
                                    {apiData.transfer.dev_holder.name}
                                </Link>
                            </td>
                            <td>
                                <b>{t("было")}:</b>{" "}
                                {DeveloperResidenceTerr[apiData.transfer.dev_transfer.residence_terr]}
                                <br />
                                <b>{t("стало")}:</b>{" "}
                                {DeveloperResidenceTerr[apiData.transfer.dev_holder.residence_terr]}
                            </td>
                            <td>
                                <b>{t("было")}:</b> {apiData.transfer.dev_transfer.company_ru}
                                <br />
                                <b>{t("стало")}:</b> {apiData.transfer.dev_holder.company_ru}
                            </td>
                            <td>
                                <b>{t("было")}:</b>{" "}
                                <DevForm.Select
                                    choices={apiData.merchant_sets.map((o) => ({ value: o.id, label: o.name }))}
                                    value={obj.game.merchant_set_id}
                                    disabled
                                />
                                <br />
                                <b>{t("стало")}:</b>{" "}
                                <DevForm.Select
                                    choices={apiData.merchant_sets.map((o) => ({ value: o.id, label: o.name }))}
                                    value={moderationData.game[obj.game_id].merchant_set_id}
                                    onChange={(data) => {
                                        const newGameData = { ...moderationData };
                                        newGameData.game[obj.game_id].merchant_set_id = data.value;
                                        setModerationData(newGameData);
                                    }}
                                    menuPortalTarget={document.body} // решает проблему z-index с чойсами
                                />
                            </td>
                            <td>
                                <b>{t("было")}:</b>{" "}
                                <DevForm.TextInput name="revenue_share" value={obj.game.revenue_share} disabled />
                                <br />
                                <b>{t("стало")}:</b>{" "}
                                <DevForm.TextInput
                                    name="new_revenue_share"
                                    value={moderationData.game[obj.game_id].revenue_share}
                                    onChange={(ev) => {
                                        const newGameData = { ...moderationData };
                                        newGameData.game[obj.game_id].revenue_share = ev.target.value;
                                        setModerationData(newGameData);
                                    }}
                                />
                            </td>
                            <td>
                                <b>{t("было")}:</b>{" "}
                                <DevForm.Select
                                    choices={Object.entries(GameTaxType).map(([value, label]) => ({
                                        value,
                                        label,
                                    }))}
                                    value={obj.game.tax_type}
                                    disabled
                                />
                                <br />
                                <b>{t("стало")}:</b>{" "}
                                <DevForm.Select
                                    choices={Object.entries(GameTaxType).map(([value, label]) => ({
                                        value,
                                        label,
                                    }))}
                                    value={moderationData.game[obj.game_id].tax_type}
                                    onChange={(data) => {
                                        const newGameData = { ...moderationData };
                                        newGameData.game[obj.game_id].tax_type = data.value;
                                        setModerationData(newGameData);
                                    }}
                                    menuPortalTarget={document.body}
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            {apiData.file_signed_transfer && (
                <>
                    <br />
                    <a href={URL.createObjectURL(Base64ToPDFBlob(apiData.file_signed_transfer.url))} target="_blank">
                        {t("ГП от отчуждателя")}
                    </a>
                </>
            )}
            <br />
            <a href={URL.createObjectURL(Base64ToPDFBlob(apiData.file_signed_holder?.url))} target="_blank">
                {t("ГП от правообладателя")}
            </a>
            <br />
            <b>{t("Комментарий отчуждателя")}</b>: {apiData.transfer.transfer_comment}
            <br />
            <b>{t("Комментарий правообладателя")}</b>: {apiData.transfer.holder_comment}
            <br />
            <b>{t("Комментарий модератора")}</b>:
            <DevForm.TextArea
                name="comment"
                value={moderationData.moder_comment}
                onChange={(ev) => setModerationData((prev) => ({ ...prev, moder_comment: ev.target.value }))}
                maxLength={1024}
            />
            <br />
            <ButtonGroup>
                <DevForm.Button variant="success" name="approved" onClick={handleSubmit}>
                    {t("Принять")}
                </DevForm.Button>
                <DevForm.Button variant="danger" name="rejected" onClick={handleSubmit}>
                    {t("Отклонить")}
                </DevForm.Button>
            </ButtonGroup>
        </ListingCard>
    );
}
